import { readStoreLocal } from "../storeScope";

export const MAX_OWNER_EMAILS = 3;

export const RECEIPT_DELIVERY = {
  PAPER: "paper",
  EMAIL: "email",
  BOTH: "both",
};

const EMAIL_PATTERN = /^[^\s@<>(),;:"[\]]+@[^\s@<>(),;:"[\]]+\.[^\s@<>(),;:"[\]]{2,}$/;

export function normalizeEmail(email) {
  return String(email || "").trim().toLowerCase();
}

export function isValidEmail(email) {
  return EMAIL_PATTERN.test(normalizeEmail(email));
}

const FAILED_RECEIPT_EMAILS = "failedReceiptEmails";

function readFailedReceiptEmails() {
  try {
    const list = JSON.parse(sessionStorage.getItem(FAILED_RECEIPT_EMAILS) || "[]");
    return Array.isArray(list) ? list.map(normalizeEmail).filter(Boolean) : [];
  } catch {
    return [];
  }
}

export function rememberFailedReceiptEmail(email) {
  const next = normalizeEmail(email);
  if (!isValidEmail(next)) return;
  const list = readFailedReceiptEmails();
  if (!list.includes(next)) sessionStorage.setItem(FAILED_RECEIPT_EMAILS, JSON.stringify([...list, next].slice(-50)));
}

export function forgetFailedReceiptEmail(email) {
  const next = normalizeEmail(email);
  sessionStorage.setItem(FAILED_RECEIPT_EMAILS, JSON.stringify(readFailedReceiptEmails().filter((item) => item !== next)));
}

export function isFailedReceiptEmail(email) {
  const next = normalizeEmail(email);
  return Boolean(next) && readFailedReceiptEmails().includes(next);
}

export function validOwnerEmails(store) {
  const list = Array.isArray(store?.receiptOwnerEmails) ? store.receiptOwnerEmails : [];
  return list.map(normalizeEmail).filter(isValidEmail).slice(0, MAX_OWNER_EMAILS);
}

/**
 * Receipt delivery options offered at payment for a store.
 * A store without the new fields keeps paper only, as before.
 */
export function getStoreReceiptConfig(store) {
  const allowed = store?.emailReceiptAllowed === true;
  const ownerEmails = validOwnerEmails(store);
  const saved = store?.receiptDelivery || {};
  const emailOn = allowed && saved.email === true && ownerEmails.length > 0;
  const paperOn = saved.paper !== false || !emailOn;
  return {
    allowed,
    ownerEmails,
    paper: paperOn,
    email: emailOn,
    showSelector: emailOn,
    modes: {
      [RECEIPT_DELIVERY.PAPER]: paperOn,
      [RECEIPT_DELIVERY.EMAIL]: emailOn,
      [RECEIPT_DELIVERY.BOTH]: paperOn && emailOn,
    },
    defaultMode: paperOn ? RECEIPT_DELIVERY.PAPER : RECEIPT_DELIVERY.EMAIL,
  };
}

export function formatFromDelivery(delivery) {
  if (delivery?.paper && delivery?.email) return RECEIPT_DELIVERY.BOTH;
  if (delivery?.email) return RECEIPT_DELIVERY.EMAIL;
  return RECEIPT_DELIVERY.PAPER;
}

export function deliveryFromFormat(format) {
  if (format === RECEIPT_DELIVERY.BOTH) return { paper: true, email: true };
  if (format === RECEIPT_DELIVERY.EMAIL) return { paper: false, email: true };
  return { paper: true, email: false };
}

export function isEmailDelivery(mode) {
  return mode === RECEIPT_DELIVERY.EMAIL || mode === RECEIPT_DELIVERY.BOTH;
}

/** MEV merchant-copy default. Temporary bills and closing receipts are separate. */
export function merchantCopyDefault(kind) {
  let settings = {};
  try {
    settings = JSON.parse(readStoreLocal("Settings_envrn") || "{}");
  } catch {
    settings = {};
  }
  if (kind === "temporary") {
    return settings.merchantCopyTemporary === true
      || (settings.merchantCopyTemporary === undefined && settings.merchantCopy === true);
  }
  return settings.merchantCopyClosing === true;
}

export function deliveryFromChoice(choice) {
  const address = normalizeEmail(choice?.emailAddress);
  const email = choice?.email === true && isValidEmail(address);
  const paper = email ? choice?.paper !== false : true;
  let receiptDelivery = RECEIPT_DELIVERY.PAPER;
  if (paper && email) receiptDelivery = RECEIPT_DELIVERY.BOTH;
  else if (email) receiptDelivery = RECEIPT_DELIVERY.EMAIL;
  return {
    receiptDelivery,
    receiptEmail: email ? address : null,
    merchantCopy: choice?.merchantCopy === true,
  };
}

export function orderCustomerEmail(order) {
  return normalizeEmail(
    order?.receiptEmail
    || order?.customerEmail
    || order?.customer?.email
    || order?.customerData?.email
    || ""
  );
}

/** Uber Eats guests are not store customers and have no receipt email. */
export function isUberEatsOrder(order) {
  const blob = [
    order?.platform,
    order?.orderChannel,
    order?.channel,
    order?.onlineOrderChannel,
    order?.sourceChannel,
  ].map((value) => String(value || "").toUpperCase()).join(" ");
  return blob.includes("UBER");
}

export function receiptChoiceDefaults(order, store, kind) {
  if (isUberEatsOrder(order)) {
    return {
      receiptDelivery: RECEIPT_DELIVERY.PAPER,
      receiptEmail: null,
      merchantCopy: true,
    };
  }
  const address = orderCustomerEmail(order);
  const emailShown = getStoreReceiptConfig(store).email && isValidEmail(address);
  return deliveryFromChoice({
    paper: true,
    email: emailShown,
    emailAddress: address,
    merchantCopy: merchantCopyDefault(kind),
  });
}

export function applyReceiptChoice(order, choice) {
  if (isUberEatsOrder(order)) {
    return {
      ...order,
      receiptDelivery: RECEIPT_DELIVERY.PAPER,
      receiptEmail: null,
      merchantCopy: true,
    };
  }
  const applied = choice?.receiptDelivery
    ? {
      receiptDelivery: choice.receiptDelivery,
      receiptEmail: choice.receiptEmail || null,
      merchantCopy: choice.merchantCopy === true,
    }
    : receiptChoiceDefaults(order, null, "closing");
  return { ...order, ...applied };
}
